/**
 * PWA persistence helpers — request the OS-level
 * `navigator.storage.persist()` permission so the browser
 * promotes our origin's `localStorage` / IndexedDB / Cache
 * Storage tier from "best effort" to "persistent" (Chrome /
 * Firefox / Safari 17+ semantics). Without this promotion the
 * runtime cache the SW precaches can be evicted under storage
 * pressure mid-session — losing the offline shell + the auth
 * tokens that ride localStorage on a cold reconnect.
 *
 * The estimate helper sits alongside so a future quota-warning
 * surface can read `usage / quota` directly without re-implementing
 * the wrap. No UI today — the boot hook just fires the request +
 * logs the outcome via `console.debug` so an operator inspecting
 * DevTools sees the grant / deny verdict.
 *
 * Permission grants are device-class dependent: Chrome grants
 * silently to PWAs installed via "Add to Home screen" + may
 * prompt the user on a fresh browser-tab install. Either way
 * the API is fire-and-forget — the helper never throws + never
 * blocks the boot path.
 */

export interface PersistenceProbe {
  /** `true` once the browser actually promoted the origin to
   *  persistent storage (whether silently or via user
   *  permission). `false` when the grant denied, the API was
   *  unsupported, or the helper short-circuited under SSR /
   *  test. */
  readonly persisted: boolean;
  /** Bytes the origin currently occupies across every storage
   *  tier (localStorage + IndexedDB + Cache Storage + ...).
   *  `null` when the estimate API is unsupported. */
  readonly usageBytes: number | null;
  /** Origin quota ceiling in bytes. `null` when the estimate
   *  API is unsupported. */
  readonly quotaBytes: number | null;
}

/**
 * Probe + request persistent storage. Fire-and-forget: the boot
 * hook calls it from a microtask so the first React paint runs
 * uninterrupted. The returned probe carries the post-grant state
 * + the current quota so a future quota-warning surface can pivot
 * off the same result without re-calling the API.
 */
export async function requestPersistentStorage(): Promise<PersistenceProbe> {
  const fallback: PersistenceProbe = {
    persisted: false,
    usageBytes: null,
    quotaBytes: null,
  };
  if (typeof navigator === "undefined" || navigator.storage === undefined) {
    return fallback;
  }
  let persisted = fallback.persisted;
  try {
    if (typeof navigator.storage.persisted === "function") {
      persisted = await navigator.storage.persisted();
    }
    if (!persisted && typeof navigator.storage.persist === "function") {
      persisted = await navigator.storage.persist();
    }
  } catch {
    // Permission denied / SecurityError / unsupported context —
    // fall through with `persisted: false`. Never blocks boot.
  }
  let usageBytes = fallback.usageBytes;
  let quotaBytes = fallback.quotaBytes;
  try {
    if (typeof navigator.storage.estimate === "function") {
      const e = await navigator.storage.estimate();
      usageBytes = typeof e.usage === "number" ? e.usage : null;
      quotaBytes = typeof e.quota === "number" ? e.quota : null;
    }
  } catch {
    // Estimate denied — keep nulls.
  }
  return { persisted, usageBytes, quotaBytes };
}
